import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {alertModule, studyDefinitionModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import DataFieldCache from "../../../models/data.field.cache";
import AuditReasonOfChangeDto = MdbsCore.AuditReasonOfChangeDto;

import {
    findField,
    findParentField,
    populateCache,
    hasPopulationOnUpdate,
    updateFieldsEditable, updateFieldVisibility, findFieldForChangeReason
} from "@/auxillary/datafield.cache.helpers";
import DataFieldDto = MdbsCore.DataFieldDto;
import DataFieldDefinitionDto = MdbsCore.DataFieldDefinitionDto;
import ValidationMessageDto = MdbsCore.ValidationMessageDto;
import ITemporaryDataFieldDto = MdbsCore.ITemporaryDataFieldDto;
import {AEREPORTS} from "@/components/study/studyDefinitionIds";


@Module({namespaced: true, name: 'dataFieldsEdit', store: store})
export default class DataFieldsEditModule extends BaseModule {
    private _dataFields = new Array<DataFieldCache>();
    private _validationMessages = new Array<Readonly<ValidationMessageDto>>();
    private _temporaryFields = new Array<Readonly<ITemporaryDataFieldDto>>();
    private _editable = true;

    public get dataFields() {
        return this._dataFields;
    }

    public get validationMessages() {
        return this._validationMessages;
    }

    public get editable() {
        return this._editable;
    }

    public get temporaryFields() {
        return this._temporaryFields;
    }

    public get getValueByIdentifier() {
        return (identifier: string) =>
            this._dataFields
                .find(f => f.identifier == identifier)?.values;
    }

    public get hasChanges() {
        if (this.isAeReport)
            return this.isEditingAeReportEntry;
        else {
            return this._dataFields
                .traverse(v => v.childFields)
                .some(f => f.hasChanges);
        }
    }

    @Mutation
    public setReasonForChange(payload: { field: DataFieldCache, reasonForChange: AuditReasonOfChangeDto }) {
        findFieldForChangeReason(payload.field, this._dataFields)
            ?.setReasonForChange(payload.reasonForChange)
    }


    @Mutation
    public setValue(payload: { field: DataFieldCache, key: string, value: any }) {
        findField(payload.field, this._dataFields)
            ?.setValue(payload.key, payload.value)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public setValues(payload: { field: DataFieldCache, values: any }) {
        findField(payload.field, this._dataFields)
            ?.setValues(payload.values)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public removeValue(payload: { field: DataFieldCache, key: string }) {
        findField(payload.field, this._dataFields)
            ?.removeValue(payload.key)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public resetValues(payload: { field: DataFieldCache }) {
        findField(payload.field, this._dataFields)
            ?.resetValues()
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public deleteValues(payload: { field: DataFieldCache }) {
        findField(payload.field, this._dataFields)
            ?.deleteValues()
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public lockManually(payload: { field: DataFieldCache }) {
        findField(payload.field, this._dataFields)
            ?.lockManually()
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public unlockManually(payload: { field: DataFieldCache }) {
        findField(payload.field, this._dataFields)
            ?.unlockManually()
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public removeField(fieldToRemove: DataFieldCache) {
        const parentField = findParentField(fieldToRemove, this._dataFields)
        const field = findField(fieldToRemove, this._dataFields);
        if (parentField && field) {
            parentField.removeChild(field)
        }
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public setTag(payload: { field: DataFieldCache, key: string, value: string }) {
        findField(payload.field, this._dataFields)
            ?.setTag(payload.key, payload.value)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public setLocked(payload: { field: DataFieldCache }) {
        findField(payload.field, this._dataFields)
            ?.lockManually();
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public removeTag(payload: { field: DataFieldCache, key: string }) {
        findField(payload.field, this._dataFields)
            ?.removeTag(payload.key)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    public createChildField(payload: {
        parentField: DataFieldCache,
        temporaryFields: Array<ITemporaryDataFieldDto>,
        childFieldDefinitionId: string | undefined
    }) {
        const {parentField, temporaryFields, childFieldDefinitionId} = payload;

        const field = findField(parentField, this._dataFields)
        if (!field?.referenceId) return;
        let referenceDefinition;
        if (field.referenceType == "PatientDefinition")
            referenceDefinition = studyDefinitionModule.patientDefinition;
        else if (field.referenceType == "VisitDefinition")
            referenceDefinition = studyDefinitionModule.visitDefinitionById(field.referenceId)
        else
            referenceDefinition = studyDefinitionModule.instrumentDefinitionById(field.referenceId)

        const childFieldDefintion = referenceDefinition?.fields
            ?.find(f => !f.isDeleted && (childFieldDefinitionId ?
                f.parentFieldDefinitionId == field.definitionId && f.id == childFieldDefinitionId :
                f.parentFieldDefinitionId == field.definitionId));

        if (!childFieldDefintion) return;

        let newChildField = new DataFieldCache(childFieldDefintion, undefined)

        newChildField = populateCache(newChildField, referenceDefinition?.fields ?? [], [], temporaryFields);

        const newOrder = field.childFields.map(c => c.order).reduce((o1, o2) => (o1 > o2 ? o1 : o2), -10) + 10;
        newChildField.updateOrder(newOrder);
        field.addChild(newChildField);

        this._lastAddedChildFieldId = newChildField.id;

        updateFieldsEditable(this._dataFields, this._editable)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    setDataFields(dataFields: Array<DataFieldCache>) {
        this._dataFields.updateValues(dataFields);
        updateFieldsEditable(this._dataFields, this._editable)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    setValidationMessages(validationMessages: Array<ValidationMessageDto>) {
        this._validationMessages.updateValues(validationMessages);
    }

    @Mutation
    setTemporaryFields(temporaryFields: Array<ITemporaryDataFieldDto> | undefined) {
        // console.log("setTemporaryFields: " + JSON.stringify(temporaryFields))
        this._temporaryFields.updateValues(temporaryFields ?? []);
    }

    @Mutation
    public setEditable(editable: boolean) {
        this._editable = editable;
        updateFieldsEditable(this._dataFields, this._editable)
        updateFieldVisibility(this._dataFields);
    }

    @Mutation
    reset() {
        this._dataFields.clear();
        this._validationMessages.clear();
    }

    @Action({rawError: true})
    async loadDataFields(payload: {
        definitions: Array<Readonly<DataFieldDefinitionDto>>,
        dataFields: Array<DataFieldDto>,
        validationMessages?: Array<Readonly<ValidationMessageDto>> | undefined,
        temporaryFields?: Array<Readonly<ITemporaryDataFieldDto>> | undefined
    }) {
        this.startLoading();
        const dataFields = new Array<DataFieldCache>();
        const definitions = payload.definitions;
        const dataFieldDtos = payload.dataFields;
        this.setTemporaryFields(payload.temporaryFields);

        try {
            // load existing fields recursively
            for (let dto of dataFieldDtos) {
                const definition = definitions.find(d => d.id == dto.definitionId)
                if (definition) {
                    if (definition.isDeleted || dto.isDeleted) continue;
                    if (definition.parentFieldDefinitionId) continue;

                    const temporaryField = hasPopulationOnUpdate(definition.rules, dto) ?
                        this.temporaryFields.find(f => f.definitionId === definition.id) : undefined;

                    let newField = new DataFieldCache(definition, dto, temporaryField)
                    newField = populateCache(newField, definitions, dataFieldDtos, this.temporaryFields);
                    dataFields.push(newField)
                } else {
                    await alertModule.handleException(new Error(`data-field-definition with id: ${dto.definitionId} is missing!`))
                    this.reset();
                    this.stopLoading();
                    return
                }
            }

            // load new fields
            for (let definition of definitions) {
                if (definition.isDeleted) continue;
                if (definition.parentFieldDefinitionId) continue;
                if (dataFields.find(d => d.definitionId == definition.id)) continue;

                const temporaryField = this.temporaryFields.find(f => f.definitionId === definition.id) ?? undefined;
                let newField = new DataFieldCache(definition, undefined, temporaryField);
                newField = populateCache(newField, definitions, dataFieldDtos, this.temporaryFields);

                dataFields.push(newField)
            }
            // sort by order
            dataFields.sort((a, b) => (a.order > b.order) ? 1 : -1)

            // set editable
            this.setDataFields(dataFields);

            this.setValidationMessages(payload.validationMessages ?? [])
        } catch (e) {
            await alertModule.handleException(e)
            this.reset();
        } finally {
            this.stopLoading();
        }
    }

    // TODO refactor all stuff after this, since this are hacky solution to our save problem (resets everything)

    private _lastAddedChildFieldId: string = "";

    get lastAddedChildFieldId() {
        return this._lastAddedChildFieldId;
    }

    private _lastEditedFieldId: string = "";

    get lastEditedFieldId() {
        return this._lastEditedFieldId;
    }

    @Mutation
    setLastEditedFieldId(lastEditedFieldId: string) {
        this._lastEditedFieldId = lastEditedFieldId;
    }

    @Mutation
    resetLastEditedFieldId() {
        this._lastEditedFieldId = "";
        this._isEditingAeReport = false;
        this._editingAeReportId = "";
    }

    // What to do after saving an reloading all datafields
    private _afterSaveAction: string = ""

    get afterSaveAction() {
        return this._afterSaveAction;
    }

    @Mutation
    setAfterSaveAction(afterSaveAction: string) {
        this._afterSaveAction = afterSaveAction;
    }

    @Mutation
    resetAfterSaveAction() {
        this._afterSaveAction = "";
    }


    private _isEditingAeReport = false;
    private _editingAeReportId: string = "";

    get editingAeReportId() {
        return this._editingAeReportId;
    }

    @Mutation
    setIsEditingAeReport(isEditingAeReport: boolean) {
        this._isEditingAeReport = isEditingAeReport;
    }

    @Mutation
    setEditingAeReportId(eventReportId: string) {
        this._editingAeReportId = eventReportId;
    }

    get isAeReport() {
        return this._dataFields.some(f => f.definitionId == AEREPORTS.EVENT_REPORT_LIST);
    }

    get isEditingAeReportEntry() {
        if (!this._isEditingAeReport)
            return false;

        const report = this._dataFields
            .traverse(v => v.childFields)
            .find(r => r.id == this._editingAeReportId);

        return report != undefined ?
            report.childFields.traverse(v => v.childFields).some(f => f.hasChanges) : false;
    }
}


